import "./tracking-history.css";

import { useMsal } from "@azure/msal-react";
import { Card } from "@tql/ui-components-react/kendo/layout";
import { useState } from "react";
import { useParams } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { usePostNewTrackHistory } from "~/features/shipment-management/components/tracking-history/hooks/use-post-track-history.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type {
	FormErrors,
	TrackingHistoryProps,
} from "~/features/shipment-management/types/tracking-history-types.ts";
import type { TrackingMessageAction } from "~/features/shipment-management/utils/constants.ts";
import { TRACKING_MESSAGE_ACTIONS } from "~/features/shipment-management/utils/constants.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import type { ShipmentCheckCall } from "~/shared/schemas/shipment-management-schema";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

import { CheckCallForm } from "./components/check-call-form.tsx";
import { DeleteTrackingMessageModal } from "./components/delete-tracking-message-modal.tsx";
import { TrackingHistoryHeader } from "./components/tracking-history-header.tsx";
import { TrackingHistoryList } from "./components/tracking-history-list.tsx";
import { useDeleteTrackHistory } from "./hooks/use-delete-track-history.ts";
import { usePutTrackHistory } from "./hooks/use-put-track-history.ts";

export const TrackingHistory = ({
	carrierWebsite,
	details,
}: TrackingHistoryProps) => {
	const params = useParams();
	// The tracking-message endpoints are employee-only, so customers never get
	// edit or delete actions.
	const { isInternal } = useDeploymentType();
	const { accounts } = useMsal();
	const { showSuccess, showError } = useShipmentNotifications();
	const poNumber = params["poNumber"] ?? "";
	const currentUserName = accounts[0]?.name ?? null;
	const [message, setMessage] = useState("");
	const [selectedShipmentCheckCall, setSelectedShipmentCheckCall] =
		useState<ShipmentCheckCall | null>(null);
	const [checkCallPendingDelete, setCheckCallPendingDelete] =
		useState<ShipmentCheckCall | null>(null);
	const [selectedPostalCode, setSelectedPostalCode] = useState<ZipCode | null>(
		null,
	);
	const [postalCodeValue, setPostalCodeValue] = useState("");
	const [formErrors, setFormErrors] = useState<FormErrors>({});

	// Bumping this key remounts the self-managed PostalCodeSearch so its
	// displayed text can be cleared or seeded from state.
	const [postalResetKey, setPostalResetKey] = useState(0);

	// Shared reset for the check-call form, used after a successful mutation and
	// when the user cancels an edit.
	const resetForm = () => {
		setMessage("");
		setSelectedPostalCode(null);
		setSelectedShipmentCheckCall(null);
		setPostalCodeValue("");
		setPostalResetKey((key) => key + 1);
		setFormErrors({});
	};

	const clearFormError = (field: keyof FormErrors) => {
		setFormErrors((currentErrors) => {
			if (!currentErrors[field]) return currentErrors;

			return { ...currentErrors, [field]: undefined };
		});
	};

	const { postNewTrackHistory, isPostingNewTrackHistory } =
		usePostNewTrackHistory({ onSuccess: resetForm });

	const { putTrackHistory, isPuttingTrackHistory } = usePutTrackHistory({
		onSuccess: resetForm,
	});

	const { deleteTrackHistory, isDeletingTrackHistory } =
		useDeleteTrackHistory();

	const isSaving =
		isPostingNewTrackHistory || isPuttingTrackHistory || isDeletingTrackHistory;

	const handleCheckCallSelect = (
		checkCall: ShipmentCheckCall,
		action: TrackingMessageAction,
	) => {
		if (action === TRACKING_MESSAGE_ACTIONS.delete) {
			setCheckCallPendingDelete(checkCall);
			return;
		}
		setSelectedShipmentCheckCall(checkCall);
		setMessage(checkCall.remark ?? "");
		const location = [checkCall.city, checkCall.state, checkCall.country]
			.filter(Boolean)
			.join(", ");
		// Seed the search field's displayed value and remount it so the selected
		// check call's location shows in the otherwise self-managed input.
		setPostalCodeValue(
			checkCall.zip ? `${checkCall.zip.trim()} - ${location}` : location,
		);
		setPostalResetKey((key) => key + 1);
	};

	const handleConfirmDelete = () => {
		if (checkCallPendingDelete) {
			deleteTrackHistory({ checkCall: checkCallPendingDelete, poNumber });
		}
		setCheckCallPendingDelete(null);
	};

	const handleMessageChange = (value: string) => {
		setMessage(value);
		clearFormError("message");
	};

	const handlePostalSelectionChange = (zipCode: ZipCode | null) => {
		setSelectedPostalCode(zipCode);
		clearFormError("location");
	};

	const handleSubmit = () => {
		if (selectedShipmentCheckCall) {
			putTrackHistory({
				trackHistory: selectedShipmentCheckCall,
				poNumber,
				message,
				selectedZipCode: selectedPostalCode,
			});
		} else {
			const errors: FormErrors = {};
			if (!selectedPostalCode) errors.location = "Location is required.";
			if (!message.trim()) errors.message = "Message is required.";
			if (errors.location || errors.message) {
				setFormErrors(errors);
				return;
			}
			postNewTrackHistory({
				message,
				poNumber,
				postalCodeData: selectedPostalCode,
			});
		}
	};

	const handleCopyTrackingLink = async () => {
		const trackingLinkUrl = `${globalThis.location.origin}${getBaseUrl()}${
			routes.trackShipment
		}?searchNumber=${params["poNumber"] ?? ""}`;
		try {
			await navigator.clipboard.writeText(trackingLinkUrl);
			showSuccess("Tracking link copied to clipboard.");
		} catch {
			showError("Failed to copy tracking link. Please try again.");
		}
	};

	return (
		<Card>
			<TrackingHistoryHeader
				carrierWebsite={carrierWebsite}
				isSaving={isSaving}
				onCopyLink={() => void handleCopyTrackingLink()}
			/>
			<CheckCallForm
				isEditing={selectedShipmentCheckCall != null}
				isSaving={isSaving}
				locationError={formErrors.location}
				message={message}
				messageError={formErrors.message}
				onCancel={resetForm}
				onMessageChange={handleMessageChange}
				onPostalSelectionChange={handlePostalSelectionChange}
				onSubmit={handleSubmit}
				postalCodeValue={postalCodeValue}
				postalResetKey={postalResetKey}
			/>
			<TrackingHistoryList
				canManageMessages={isInternal}
				currentUserName={currentUserName}
				details={details}
				isSaving={isSaving}
				onSelect={handleCheckCallSelect}
			/>
			{checkCallPendingDelete ? (
				<DeleteTrackingMessageModal
					checkCall={checkCallPendingDelete}
					onClose={() => setCheckCallPendingDelete(null)}
					onConfirm={handleConfirmDelete}
				/>
			) : null}
		</Card>
	);
};
